/* eslint-disable react/prop-types */
import { useEffect, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useWakeLock } from 'react-screen-wake-lock';
import InVisitButton from './InVisitButton';
import AfterVisitButton from './AfterVisitButton';
import { useAuth } from '@frontegg/react';
import { MicSettings } from 'src/components/MicSettings';
import { analytics } from 'src/services';
import { EncounterPreChart } from 'src/pages/encounter/details';

const CurrentTranscript = forwardRef(
  (
    {
      topBarInputs,
      storedParams,
      selectedRecord,
      encounterStatus,
      encounterPhase,
      setEncounterStatus,
      setLiveTranscript,
      setRecord,
      setLivePartialTranscript,
      setActiveRightPanelTab,
      setIsVisitStreaming,
      setIsChartStreaming,
      isVisitStreaming,
      isChartStreaming,
      isRecording,
      isChartRecording,
      isVisitRecording,
      setIsVisitRecording,
      setIsChartRecording,
      schedulepage,
      restrictTemplates,
      autoResumeVisit,
      patientDetails,
      settingsOrgValue,
      searchFilters,
      showSaveDeleteButtons,
      setShowSaveDeleteButtons,
    },
    ref
  ) => {
    const { mobileViewStatus } = useSelector((state) => state?.encounterState);

    const { isSupported, released, request, release } = useWakeLock();
    const [selectedMicrophone, setSelectedMicrophone] = useState(
      localStorage.getItem('preferredMicrophoneDeviceId') || 'default'
    );
    const record =
      selectedRecord !== undefined ? selectedRecord : mobileViewStatus;

    const { user } = useAuth();

    useEffect(() => {
      if (!isSupported) {
        console.warn('Screen Wake Lock is not supported');
        return;
      }
      if (isRecording) {
        request().catch((err) => {
          console.error(err);
        });
      } else {
        if (!released) {
          release();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording, request, release]);

    const handleMicrophoneChange = async (deviceId) => {
      if (schedulepage) {
        // eslint-disable-next-line no-undef
        analytics.track(
          'Clicked Microphone Option In Microphone Setting From VCA',
          { email: user?.email }
        );
      } else {
        // eslint-disable-next-line no-undef
        analytics.track(
          'Clicked Microphone Option In Microphone Setting From Aura',
          { email: user?.email }
        );
      }
      setSelectedMicrophone(deviceId);
      localStorage.setItem('preferredMicrophoneDeviceId', deviceId);
    };

    const renderRecordingControls = () => {
      if (encounterPhase === 'pre-chart') {
        return (
          <EncounterPreChart
            selectedMicrophone={selectedMicrophone}
            key={record?.encounter_id}
            encounterId={record?.encounter_id}
            mode={schedulepage ? 'vca' : 'aura'}
            legacySearchFilters={storedParams}
          />
        );
      }

      if (
        encounterStatus === 'completed' ||
        encounterStatus === 'summary_inprogress'
      ) {
        return (
          <InVisitButton
            selectedMicrophone={selectedMicrophone}
            encounterPhase={encounterPhase}
            autoResumeVisit={autoResumeVisit}
            encounterStatus={encounterStatus}
            storedParams={storedParams}
            selectedRecord={record}
            topBarInputs={topBarInputs}
            setRecord={setRecord}
            setEncounterStatus={setEncounterStatus}
            setLiveTranscript={setLiveTranscript}
            setLivePartialTranscript={setLivePartialTranscript}
            setActiveRightPanelTab={setActiveRightPanelTab}
            isStreaming={isVisitStreaming}
            setIsStreaming={setIsVisitStreaming}
            isRecording={isVisitRecording}
            setIsRecording={setIsVisitRecording}
            schedulepage={schedulepage}
            restrictTemplates={restrictTemplates}
            noTemplate={true}
            ref={ref}
          />
        );
      }

      if (encounterPhase === 'in-visit') {
        return (
          <InVisitButton
            autoResumeVisit={autoResumeVisit}
            selectedMicrophone={selectedMicrophone}
            encounterPhase={encounterPhase}
            encounterStatus={encounterStatus}
            storedParams={storedParams}
            selectedRecord={record}
            topBarInputs={topBarInputs}
            setRecord={setRecord}
            setEncounterStatus={setEncounterStatus}
            setLiveTranscript={setLiveTranscript}
            setLivePartialTranscript={setLivePartialTranscript}
            setActiveRightPanelTab={setActiveRightPanelTab}
            isStreaming={isVisitStreaming}
            setIsStreaming={setIsVisitStreaming}
            isRecording={isVisitRecording}
            setIsRecording={setIsVisitRecording}
            schedulepage={schedulepage}
            restrictTemplates={restrictTemplates}
            ref={ref}
            showSaveDeleteButtons={showSaveDeleteButtons}
            setShowSaveDeleteButtons={setShowSaveDeleteButtons}
          />
        );
      }

      if (encounterPhase === 'after-visit') {
        return (
          <AfterVisitButton
            selectedMicrophone={selectedMicrophone}
            encounterPhase={encounterPhase}
            encounterStatus={encounterStatus}
            storedParams={storedParams}
            selectedRecord={record}
            topBarInputs={topBarInputs}
            setRecord={setRecord}
            setEncounterStatus={setEncounterStatus}
            setLiveTranscript={setLiveTranscript}
            setLivePartialTranscript={setLivePartialTranscript}
            setActiveRightPanelTab={setActiveRightPanelTab}
            isStreaming={isVisitStreaming}
            setIsStreaming={setIsVisitStreaming}
            isRecording={isVisitRecording}
            setIsRecording={setIsVisitRecording}
            schedulepage={schedulepage}
            restrictTemplates={restrictTemplates}
            searchFilters={searchFilters}
          />
        );
      }
    };

    return (
      <>
        {!isRecording && encounterPhase != 'after-visit' && (
          <div
            className="absolute top-[-50px] md:top-[-70px] lg:top-2 right-3"
            id="Microphone_Open"
          >
            <MicSettings
              disabled={isRecording}
              selectedMicrophone={selectedMicrophone}
              onSelectionChange={handleMicrophoneChange}
            />
          </div>
        )}
        {renderRecordingControls()}
      </>
    );
  }
);

CurrentTranscript.displayName = 'CurrentTranscript';

export default CurrentTranscript;
